import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    AccessTimeOutlined,
    DirectionsRunOutlined,
    DoneOutlined,
    FitnessCenterOutlined
} from '@mui/icons-material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
    getActivityPeriodSummary,
    getLatestExerciseSession
} from '../../../../services/activity';
import OverviewCard from './OverviewCard';

const getActivePlan = (plans) => {
    const today = dayjs();

    return [...plans]
        .filter(
            (plan) =>
                plan.accepted &&
                dayjs(plan.start_date).isValid() &&
                dayjs(plan.end_date).isValid() &&
                !dayjs(plan.start_date).isAfter(today, 'day') &&
                !dayjs(plan.end_date).isBefore(today, 'day')
        )
        .sort(
            (firstPlan, secondPlan) =>
                dayjs(secondPlan.start_date).valueOf() -
                dayjs(firstPlan.start_date).valueOf()
        )[0];
};

const getPlanProgress = (plan) => {
    if (!plan) return null;

    const start = dayjs(plan.start_date).startOf('day');
    const end = dayjs(plan.end_date).startOf('day');
    const today = dayjs().startOf('day');
    const totalWeeks = Math.max(1, Math.ceil((end.diff(start, 'day') + 1) / 7));
    const currentWeek = Math.min(
        totalWeeks,
        Math.max(1, Math.floor(today.diff(start, 'day') / 7) + 1)
    );

    return { currentWeek, totalWeeks };
};

const getAdherenceColors = (adherence) => {
    if (adherence == null) return { color: '#56656D', background: '#EEF1F3' };
    if (adherence >= 80) return { color: '#168A0D', background: '#E2F2DA' };
    if (adherence >= 60) return { color: '#A86200', background: '#FFF4DE' };
    return { color: '#D32F2F', background: '#FDECEC' };
};

const ActivityOverviewCard = ({
    patientUuid,
    plans,
    plansLoading,
    plansError,
    onViewActivity
}) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const activePlan = useMemo(() => getActivePlan(plans), [plans]);
    const planProgress = getPlanProgress(activePlan);
    const periodStart = activePlan?.start_date || dayjs().subtract(1, 'year');
    const periodStartKey = dayjs(periodStart).format('YYYY-MM-DD');
    const todayKey = dayjs().format('YYYY-MM-DD');

    const activityQuery = useQuery({
        queryKey: [
            'patient-activity-summary',
            patientUuid,
            activePlan?.uuid || 'past-plans',
            periodStartKey,
            todayKey
        ],
        queryFn: async () => {
            const [summary, latestSession] = await Promise.all([
                getActivityPeriodSummary({
                    queryClient,
                    patientUuid,
                    planUuid: activePlan?.uuid,
                    startDate: periodStartKey,
                    endDate: dayjs()
                }),
                getLatestExerciseSession({
                    patientUuid,
                    planUuid: activePlan?.uuid,
                    startDate: periodStartKey,
                    endDate: dayjs()
                })
            ]);

            return {
                completedExercises: summary.completed,
                missedExercises: summary.missed,
                latestSession
            };
        },
        enabled: Boolean(patientUuid) && !plansLoading && !plansError,
        staleTime: 60 * 1000,
        refetchOnMount: 'always'
    });

    const completedExercises = activityQuery.data?.completedExercises || 0;
    const missedExercises = activityQuery.data?.missedExercises || 0;
    const totalExercises = completedExercises + missedExercises;
    const adherence =
        totalExercises > 0
            ? Math.round((completedExercises / totalExercises) * 100)
            : null;
    const adherenceColors = getAdherenceColors(adherence);
    const latestSession = activityQuery.data?.latestSession || null;
    const isLoading = plansLoading || (!plansError && activityQuery.isPending);
    const isError = plansError || activityQuery.isError;

    const metrics = [
        {
            key: 'total',
            icon: <FitnessCenterOutlined fontSize="small" />,
            label: t('plans.totalExercises'),
            value: totalExercises
        },
        {
            key: 'completed',
            icon: <DoneOutlined fontSize="small" />,
            label: t('plans.completedExercises'),
            value: completedExercises
        },
        {
            key: 'missed',
            icon: <AccessTimeOutlined fontSize="small" />,
            label: t('plans.notPerformed'),
            value: missedExercises
        }
    ];

    return (
        <OverviewCard
            title={t('userPage.overview.activityAndPlan')}
            icon={<DirectionsRunOutlined fontSize="small" />}
            minHeight={230}
            action={
                isLoading ? (
                    <Skeleton variant="rounded" width={132} height={36} />
                ) : !isError ? (
                    <Box
                        sx={{
                            display: 'flex',
                            minWidth: 132,
                            minHeight: 36,
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 1.75,
                            py: 0.75,
                            color: adherenceColors.color,
                            bgcolor: adherenceColors.background,
                            borderRadius: 1
                        }}
                    >
                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                            {t('userPage.overview.activitySummary.adherence')}{' '}
                            <Box component="span" sx={{ fontWeight: 800 }}>
                                {adherence == null ? '—' : `${adherence}%`}
                            </Box>
                        </Typography>
                    </Box>
                ) : null
            }
        >
            {isLoading ? (
                <Box sx={{ display: 'grid', gap: 1 }}>
                    <Skeleton width="58%" height={24} />
                    <Skeleton variant="rounded" height={82} />
                    <Skeleton width="35%" height={20} />
                </Box>
            ) : isError ? (
                <Box
                    sx={{
                        display: 'flex',
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5
                    }}
                >
                    <Typography color="error">
                        {t('userPage.overview.activitySummary.loadError')}
                    </Typography>
                    {!plansError && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => activityQuery.refetch()}
                        >
                            {t('userPage.overview.measurements.retry')}
                        </Button>
                    )}
                </Box>
            ) : (
                <>
                    <Typography
                        variant="body2"
                        noWrap
                        title={activePlan?.name || ''}
                        sx={{
                            minWidth: 0,
                            pl: 4,
                            mb: 1.25
                        }}
                    >
                        <Box component="span" sx={{ color: '#263238', fontWeight: 700 }}>
                            {activePlan?.name ||
                                t('userPage.overview.activitySummary.noActivePlanTitle')}
                        </Box>
                        <Box
                            component="span"
                            sx={{ color: 'text.secondary', fontWeight: 600 }}
                        >
                            {activePlan
                                ? t('userPage.overview.activitySummary.planWeek', {
                                      current: planProgress.currentWeek,
                                      total: planProgress.totalWeeks
                                  })
                                : t('userPage.overview.activitySummary.historyPeriod')}
                        </Box>
                    </Typography>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                            flex: 1,
                            alignItems: 'center'
                        }}
                    >
                        {metrics.map((metric, index) => (
                            <Box
                                key={metric.key}
                                sx={{
                                    minWidth: 0,
                                    px: 1.5,
                                    textAlign: 'center',
                                    borderLeft: index === 0 ? 0 : '1px solid #D9DEE2'
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        color: 'primary.main',
                                        mb: 0.25
                                    }}
                                >
                                    {metric.icon}
                                </Box>
                                <Typography
                                    variant="caption"
                                    noWrap
                                    title={metric.label}
                                    sx={{ color: '#263238', fontWeight: 700 }}
                                >
                                    {metric.label}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#263238' }}>
                                    {metric.value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            mt: 'auto',
                            pt: 1
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            {latestSession
                                ? t('userPage.overview.activitySummary.latestSession', {
                                      date: dayjs(latestSession.ti).format('DD/MM/YYYY')
                                  })
                                : t(
                                      activePlan
                                          ? 'userPage.overview.activitySummary.noPlanActivity'
                                          : 'userPage.overview.activitySummary.noActivity'
                                  )}
                        </Typography>
                        <Button
                            size="small"
                            endIcon={<ArrowForwardIcon fontSize="small" />}
                            onClick={onViewActivity}
                            sx={{ flexShrink: 0, px: 0.5, fontWeight: 600 }}
                        >
                            {t('userPage.overview.viewActivity')}
                        </Button>
                    </Box>
                </>
            )}
        </OverviewCard>
    );
};

ActivityOverviewCard.propTypes = {
    patientUuid: PropTypes.string.isRequired,
    plans: PropTypes.arrayOf(PropTypes.object).isRequired,
    plansLoading: PropTypes.bool.isRequired,
    plansError: PropTypes.bool.isRequired,
    onViewActivity: PropTypes.func.isRequired
};

export default ActivityOverviewCard;
